import { Form, FormInstance, Input, Select } from 'antd';
import React, { useEffect, useState } from 'react';
import { useTranslation } from '../../../i18n/i18n';
import useTenantStore, { ALL_TENANTS_VALUE } from '../../../stores/tenant.store';
import { userApi } from '../../../api/user.api';
import { UserRole } from '../../../gen/proto/api/gateway/v1/user_pb';
import useAuthStore from '../../../stores/auth.store';
import { CreateInstallationLocationFormValues } from './CreateInstallationLocationModal';

const PAGE_INDEX = 1;
const PAGE_SIZE = 100;
const MAX_LON = 180;
const MAX_LAT = 90;
const userAdmin = 'userAdmin';

interface FormGeneralDataProps {
  form: FormInstance<CreateInstallationLocationFormValues>;
  isModalOpen: boolean;
}

const FormGeneralData = ({ form, isModalOpen }: FormGeneralDataProps) => {
  const { t } = useTranslation('installation-location', { keyPrefix: 'modal' });

  const isAll = useTenantStore((state) => state.selectedTenant === ALL_TENANTS_VALUE);
  const tenants = useTenantStore((state) => state.tenants);
  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const isAdmin = useAuthStore((state) => state.isAdmin);
  const currentUserUuid = useAuthStore((state) => state.uuid);
  const currentUserName = useAuthStore((state) => state.username);

  const [adminOptions, setAdminOptions] = useState<{ value: string; label: string }[]>([]);
  const formTenant = Form.useWatch('tenant', form);
  const effectiveTenant = isAll ? formTenant : selectedTenant;

  useEffect(() => {
    if (!isModalOpen) return;
    if (isAdmin && currentUserUuid) {
      setAdminOptions([{ value: currentUserUuid, label: currentUserName }]);
      form.setFieldValue(userAdmin, currentUserUuid);
      return;
    }
    form.setFieldValue(userAdmin, undefined);
    setAdminOptions([]);
    if (!effectiveTenant) return;
    let active = true;
    userApi
      .listUsersByPage(PAGE_INDEX, PAGE_SIZE)
      .then((response) => {
        if (!active) return;
        const admins = response.users
          .filter((u) => u.role === UserRole.ADMIN && u.tenantUuid === effectiveTenant)
          .map((u) => ({ value: u.uuid, label: u.name }));
        setAdminOptions(admins);
      })
      .catch(() => {
        if (active) setAdminOptions([]);
      });
    return () => {
      active = false;
    };
  }, [isModalOpen, effectiveTenant, isAdmin, currentUserUuid, currentUserName, form]);

  const rangeValidator = (min: number, max: number, errorKey: string) => (_: unknown, value: string) => {
    if (!value) return Promise.resolve();
    const num = parseFloat(value);
    if (isNaN(num) || num < min || num > max) return Promise.reject(t(errorKey));
    return Promise.resolve();
  };

  return (
    <>
      <Form.Item name="name" label={t('name')} rules={[{ required: true }]}>
        <Input placeholder={t('placeholderName')} />
      </Form.Item>
      <Form.Item name="id" label={t('id')} rules={[{ required: true }]}>
        <Input placeholder={t('placeholderPlaceId')} />
      </Form.Item>
      <Form.Item name="address" label={t('address')} rules={[{ required: true }]}>
        <Input placeholder={t('placeholderAddress')} />
      </Form.Item>
      <Form.Item
        name="postalCode"
        label={t('postalCode')}
        rules={[{ required: true }, { pattern: /^\d+$/, message: t('errorInteger') }]}
      >
        <Input placeholder={t('placeholderPostalCode')} />
      </Form.Item>
      <Form.Item name="city" label={t('poblation')} rules={[{ required: true }]}>
        <Input placeholder={t('placeholderCity')} />
      </Form.Item>
      <Form.Item
        name="lat"
        label={t('lat')}
        rules={[
          { required: true },
          { pattern: /^-?\d+(\.\d+)?$/, message: t('errorFloat') },
          { validator: rangeValidator(-MAX_LAT, MAX_LAT, 'errorLatRange') },
        ]}
      >
        <Input placeholder={t('placeholderLatitude')} />
      </Form.Item>
      <Form.Item
        name="lon"
        label={t('lon')}
        rules={[
          { required: true },
          { pattern: /^-?\d+(\.\d+)?$/, message: t('errorFloat') },
          { validator: rangeValidator(-MAX_LON, MAX_LON, 'errorLonRange') },
        ]}
      >
        <Input placeholder={t('placeholderLongitude')} />
      </Form.Item>

      {isAll && (
        <Form.Item name="tenant" label={t('tenant')} rules={[{ required: true }]}>
          <Select options={tenants} />
        </Form.Item>
      )}
      <Form.Item name="userAdmin" label={t('admin')} rules={[{ required: true }]}>
        <Select options={adminOptions} disabled={isAdmin} />
      </Form.Item>
    </>
  );
};

export default FormGeneralData;
